/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-user.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import { useBoolean, useTranslation } from 'hooks';
import { OrganisationMemberFields, OrganisationAndStudyMemberFields } from 'queries/generated/fragments.generated';
import {
    INVITE_ORGANISATION_MEMBER,
    SAVE_ORGANISATION_MEMBER,
    RESEND_INVITE,
    GET_ORGANISATION_AND_STUDY_MEMBERS,
} from 'queries/uam';
import { Authentication, OrganisationMemberStatus } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { checkValidity } from 'utils/shared';
import { validateFields } from 'utils/validation';

import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogTitle, DialogContent, DialogActions } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import CustomPhoneInput from 'views/components/core/phone-input';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';

import './styles.scss';

const INVITED = 'INVITED';

const AddUserModal = ({
    onClose,
    open,
    user,
    organisation,
    onExistEmail,
    loggedUser,
}: {
    onClose: Function;
    open: boolean;
    user: OrganisationMemberFields;
    organisation: OrganisationAndStudyMemberFields;
    onExistEmail: Function;
    loggedUser: any;
}) => {
    const { t } = useTranslation();
    const initialState = {
        role: Constants.ObjectId.Empty,
        active: false,
        isAdmin: false,
        isAuditAdmin: false,
        isReportAdmin: false,
        isObsAndLogsAdmin: false,
        isTrialOsAdmin: false,
        email: '',
        phone: '',
        firstName: '',
        lastName: '',
    };

    const disabledSave = useBoolean(true);
    const saving = useBoolean(false);
    const isResent = useBoolean(true);
    const [phoneError, setPhoneError] = useState(null);

    const [inviteMember] = useMutation(INVITE_ORGANISATION_MEMBER);
    const [saveMember] = useMutation(SAVE_ORGANISATION_MEMBER);
    const [resendMember] = useMutation(RESEND_INVITE);

    const [errors, setErrors] = useState({} as any);

    const [state, setState] = useState({ ...initialState });

    const isSso = organisation?.security?.authenticationMethod === Authentication.SSO;

    const showAddUserDialog = useBoolean(true);
    const showUpdateEmailDialog = useBoolean(false);

    const organisationRoles =
        organisation?.roles
            .filter((item: any) => item.active)
            .map((item: any) => ({
                id: item.id,
                text: item.name,
            })) ?? [];

    organisationRoles.unshift({
        id: Constants.ObjectId.Empty,
        text: t('selectRole'),
    });

    useEffect(() => {
        if (open && user)
            setState({
                role: user.roleId || state.role,
                active: user.isActive || state.active,
                isAdmin: user.isAdmin || state.isAdmin,
                isAuditAdmin: user.isAuditAdmin || state.isAuditAdmin,
                isReportAdmin: user.isReportAdmin || state.isReportAdmin,
                isObsAndLogsAdmin: user.isObsAndLogsAdmin || state.isObsAndLogsAdmin,
                isTrialOsAdmin: user.isTrialOsAdmin || state.isTrialOsAdmin,
                email: user.user?.email || user.email || state.email,
                phone: user.user?.phone || user.phone || state.phone,
                firstName: user.user?.firstName || user.firstName || state.firstName,
                lastName: user.user?.lastName || user.lastName || state.lastName,
            });
        if (open && !user) setState({ ...initialState });
    }, [user, open]);

    const close = (action?: string | boolean) => {
        if (action !== 'error' && action !== 'resent') {
            setState({ ...initialState });
            setErrors({});
            isResent.setTrue();
            disabledSave.setTrue();
        }
        onClose(action);
    };

    const onToggle = (name: string, value: boolean) => {
        disabledSave.setFalse();
        setState({
            ...state,
            [name]: value,
        });
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const error = validateFields(value, field, length, required);
        setErrors({
            ...errors,
            ...error,
        });
    };

    const onChange = (name: string) => (event: any) => {
        disabledSave.setFalse();
        let length = 50;
        if (name === 'email') length = 320;

        setState({
            ...state,
            [name]: event.target.value,
        });
        const isValidEmail =
            name === 'email' && event.target.value.length ? checkValidity(event.target.value, { isEmail: true }) : true;
        if (!isValidEmail) {
            setErrors({
                ...errors,
                email: t('invalidEmail'),
            });
        } else {
            validate(event.target.value, name, length, false);
        }
    };

    const validateOnSubmit = () => {
        const userEmail = user?.email?.trim().toLowerCase() ?? '';
        const isExistEmail = organisation.members.findIndex(
            (item: any) => item.email?.trim().toLowerCase() === state.email?.trim().toLowerCase(),
        );

        if (isExistEmail >= 0 && userEmail !== state.email) {
            disabledSave.setTrue();
            onExistEmail();
            return false;
        }

        if (phoneError) {
            setErrors({
                ...errors,
                phone: phoneError,
            });
            return false;
        }

        return true;
    };

    const shouldShowUpdateEmailDialog = () => {
        const userEmail = user?.email?.trim().toLowerCase() ?? '';

        return isSso && user && userEmail !== state.email;
    };

    const save = () => {
        saving.setTrue();
        if (!user) {
            inviteMember({
                variables: {
                    roleId: state.role,
                    isAdmin: state.isAdmin,
                    isAuditAdmin: state.isAuditAdmin,
                    isReportAdmin: state.isReportAdmin,
                    isObsAndLogsAdmin: state.isObsAndLogsAdmin,
                    isTrialOsAdmin: state.isTrialOsAdmin,
                    email: state.email,
                    sendEmail: true,
                    studyIds: [],
                    active: state.active,
                    firstName: state.firstName,
                    lastName: state.lastName,
                    phone: state.phone,
                },
                refetchQueries: [
                    {
                        query: GET_ORGANISATION_AND_STUDY_MEMBERS,
                    },
                ],
            })
                .then(() => {
                    close('success');
                    saving.setFalse();
                    showAddUserDialog.setTrue();
                    showUpdateEmailDialog.setFalse();
                })
                .catch(() => {
                    close('error');
                    saving.setFalse();
                    showAddUserDialog.setTrue();
                    showUpdateEmailDialog.setFalse();
                });
        } else {
            saveMember({
                variables: {
                    memberId: user.id,
                    recordId: user.recordId,
                    active: state.active,
                    isAuditAdmin: state.isAuditAdmin,
                    isReportAdmin: state.isReportAdmin,
                    isObsAndLogsAdmin: state.isObsAndLogsAdmin,
                    isTrialOsAdmin: state.isTrialOsAdmin,
                    firstName: state.firstName,
                    lastName: state.lastName,
                    email: state.email,
                    phone: state.phone,
                    roleId: state.role,
                    isAdmin: state.isAdmin,
                },
                refetchQueries: [
                    {
                        query: GET_ORGANISATION_AND_STUDY_MEMBERS,
                    },
                ],
            })
                .then(() => {
                    close('edit');
                    saving.setFalse();
                    showAddUserDialog.setTrue();
                    showUpdateEmailDialog.setFalse();
                    const isNotChanged =
                        user.email === state.email &&
                        user.isActive === state.active &&
                        user.roleId === state.role &&
                        user.isAuditAdmin === state.isAuditAdmin &&
                        user.isReportAdmin === state.isReportAdmin &&
                        user.isObsAndLogsAdmin === state.isObsAndLogsAdmin &&
                        user.isTrialOsAdmin === state.isTrialOsAdmin &&
                        user.isAdmin === state.isAdmin &&
                        (organisation.security.twoFactorEnabled ? user.phone === state.phone : true);
                    if (isNotChanged && user?.userId === loggedUser?.id) {
                        loggedUser.setUser({
                            ...loggedUser,
                            firstName: state.firstName,
                            lastName: state.lastName,
                        });
                    }
                })
                .catch(() => {
                    close('error');
                    saving.setFalse();
                    showAddUserDialog.setTrue();
                    showUpdateEmailDialog.setFalse();
                });
        }
    };

    const submit = () => {
        if (shouldShowUpdateEmailDialog()) {
            showUpdateEmailDialog.setTrue();
            showAddUserDialog.setFalse();
        } else if (validateOnSubmit()) {
            save();
        }
    };

    const resendInvite = () => {
        resendMember({
            variables: {
                orgMemberId: user.id,
            },
        })
            .then(() => {
                close('resent');
            })
            .catch(() => {
                close('error');
            });
    };

    const getStatus = (status: string) =>
        status === OrganisationMemberStatus.VERIFYING || status === OrganisationMemberStatus.JOINED
            ? 'Verified'.toUpperCase()
            : status;

    const setIsValidNumber = (value: string | null) => {
        setPhoneError(value);
    };

    return (
        <>
            {showAddUserDialog.value && (
                <CustomDialog
                    onClose={() => close()}
                    aria-labelledby="add-user-title"
                    fullWidth
                    scroll="paper"
                    maxWidth="md"
                    open={open}
                >
                    <DialogTitle id="add-user-title">
                        {user ? (
                            <span className="edit-user-wrapper">
                                <p className="title">{`${t('uam:updateUser')}: ${user.recordId}`}</p>
                                {user.status ? <span className="user-status">{getStatus(user.status)}</span> : null}
                            </span>
                        ) : (
                            t('uam:addUser')
                        )}
                        <FontAwesomeIcon
                            icon={['fal', 'times']}
                            className="fa-lg close-icon-cv"
                            onClick={() => close(false)}
                            data-testid="close-dialog"
                        />
                    </DialogTitle>
                    <DialogContent className="add-risk-dialog-content edit-risk">
                        <div className="checkboxes-wrapper">
                            <div className="left-part">
                                <Checkbox
                                    rounded={null}
                                    id="user-is-active"
                                    className="cell-checkbox no-border"
                                    checked={state.active}
                                    onChange={() => {
                                        onToggle('active', !state.active);
                                    }}
                                    label={t('active')}
                                    labelClassName="mr-20"
                                    data-testid="user-is-active"
                                    size="medium"
                                />
                                <Checkbox
                                    rounded={null}
                                    id="user-is-audit-admin"
                                    className="cell-checkbox no-border"
                                    checked={state.isAuditAdmin}
                                    onChange={() => {
                                        onToggle('isAuditAdmin', !state.isAuditAdmin);
                                    }}
                                    label={t('uam:auditLogAccess')}
                                    labelClassName="mr-20"
                                    data-testid="user-is-audit-admin"
                                    size="medium"
                                />
                                <Checkbox
                                    rounded={null}
                                    id="user-is-administrator"
                                    className="cell-checkbox no-border"
                                    checked={state.isAdmin}
                                    onChange={() => {
                                        onToggle('isAdmin', !state.isAdmin);
                                    }}
                                    label={t('uam:userAdministratorOrganisation')}
                                    labelClassName="mr-20"
                                    data-testid="user-is-administrator"
                                    size="medium"
                                />
                                <Checkbox
                                    rounded={null}
                                    id="user-is-report-admin"
                                    className="cell-checkbox no-border"
                                    checked={state.isReportAdmin}
                                    onChange={() => {
                                        onToggle('isReportAdmin', !state.isReportAdmin);
                                    }}
                                    label={t('uam:reportAdmin')}
                                    data-testid="user-is-report-admin"
                                    size="medium"
                                />
                            </div>
                        </div>
                        <div className="checkboxes-wrapper">
                            <div className="left-part">
                                <Checkbox
                                    rounded={null}
                                    id="user-is-obs-and-logs-admin"
                                    className="cell-checkbox no-border"
                                    checked={state.isObsAndLogsAdmin}
                                    onChange={() => {
                                        onToggle('isObsAndLogsAdmin', !state.isObsAndLogsAdmin);
                                    }}
                                    label={t('uam:ObsAndLogsAdmin')}
                                    labelClassName="mr-20"
                                    data-testid="user-is-obs-and-logs-admin"
                                    size="medium"
                                />
                                <Checkbox
                                    rounded={null}
                                    id="user-is-trial-os-admin"
                                    className="cell-checkbox no-border"
                                    checked={state.isTrialOsAdmin}
                                    onChange={() => {
                                        onToggle('isTrialOsAdmin', !state.isTrialOsAdmin);
                                    }}
                                    label={t('uam:userAdministratorTrialOs')}
                                    data-testid="user-is-trial-os-admin"
                                    size="medium"
                                />
                            </div>
                        </div>
                        <div className="type-edc-wrapper mt">
                            <FormControl variant="outlined" className="form-control-type">
                                <SelectWithCategories
                                    showWarning={false}
                                    customClassName="select-outlined-role"
                                    fieldSelect="role"
                                    onChange={onChange}
                                    label={`${t('role')}*`}
                                    filterable={false}
                                    labelField="type"
                                    error={errors.role}
                                    dataArray={organisationRoles}
                                    searchable
                                    customId="select-outlined-role"
                                    currentState={state.role}
                                />
                            </FormControl>
                        </div>
                        <div className="type-edc-wrapper no-mb">
                            <FormControl variant="outlined" className="form-control-type mr">
                                <TextField
                                    id="edit-user-email"
                                    autoFocus
                                    label={`${t('email')}*`}
                                    error={!!errors.email}
                                    fullWidth
                                    value={state.email}
                                    onChange={onChange('email')}
                                    size="small"
                                    placeholder={`${t('uam:enterEmailAddress')}*`}
                                    helperText={
                                        <CharactersRemaining
                                            id="edit-user-email"
                                            length={state.email.length}
                                            max={320}
                                            error={errors.email}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type">
                                <CustomPhoneInput
                                    customId="add-user-phone"
                                    phone={state.phone}
                                    error={errors.phone}
                                    isValid={setIsValidNumber}
                                    onChange={(event: string | null | undefined) => {
                                        const newEvent = { target: { value: event } };
                                        onChange('phone')(newEvent);
                                    }}
                                />
                            </FormControl>
                        </div>
                        <div className="type-edc-wrapper no-mb">
                            <FormControl variant="outlined" className="form-control-type mr">
                                <TextField
                                    id="edit-user-first-name"
                                    autoFocus
                                    label={`${t('firstName')}*`}
                                    error={!!errors.firstName}
                                    fullWidth
                                    value={state.firstName}
                                    onChange={onChange('firstName')}
                                    size="small"
                                    placeholder={`${t('uam:enterFirstName')}*`}
                                    helperText={
                                        <CharactersRemaining
                                            id="edit-user-first-name"
                                            length={state.firstName.length}
                                            max={50}
                                            error={errors.firstName}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type">
                                <TextField
                                    id="edit-user-last-name"
                                    autoFocus
                                    label={`${t('lastName')}*`}
                                    error={!!errors.lastName}
                                    fullWidth
                                    value={state.lastName}
                                    onChange={onChange('lastName')}
                                    size="small"
                                    placeholder={`${t('uam:enterLastName')}*`}
                                    helperText={
                                        <CharactersRemaining
                                            id="edit-user-last-name"
                                            length={state.lastName.length}
                                            max={50}
                                            error={errors.lastName}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    className="mb"
                                />
                            </FormControl>
                        </div>
                        <DialogActions className={`actions-info-wrapper-edit-cv ${!user ? 'flex-end' : ''}`}>
                            <ModalUpdatedInfo item={user} />
                            <div>
                                {user && user.status === INVITED && !isSso ? (
                                    <SaveButton
                                        id="resend-invite"
                                        className="resend-btn"
                                        disabled={!isResent.value || saving.value}
                                        text="uam:resendInvite"
                                        onClick={() => {
                                            isResent.setFalse();
                                            resendInvite();
                                        }}
                                    />
                                ) : null}
                                <SaveButton
                                    id="edit-user-save"
                                    disabled={
                                        Object.keys(errors).filter((item) => errors[item]).length > 0 ||
                                        disabledSave.value ||
                                        saving.value
                                    }
                                    text={!user ? 'common:add' : 'common:update'}
                                    onClick={() => {
                                        const errorLastName = validateFields(state.lastName, 'lastName', 60, true);
                                        const errorFirstName = validateFields(state.firstName, 'firstName', 60, true);
                                        const isValidEmail = state.email.length
                                            ? checkValidity(state.email, { isEmail: true })
                                            : true;
                                        const errorEmail = isValidEmail
                                            ? validateFields(state.email, 'email', 320, true)
                                            : {
                                                  email: t('invalidEmail'),
                                              };

                                        const errorRole = {
                                            role:
                                                state.role === Constants.ObjectId.Empty
                                                    ? t('ra:requiredFieldError')
                                                    : null,
                                        };

                                        setErrors({
                                            ...errors,
                                            ...errorLastName,
                                            ...errorFirstName,
                                            ...errorEmail,
                                            ...errorRole,
                                        });
                                        if (
                                            errorLastName?.lastName ||
                                            errorFirstName?.firstName ||
                                            errorEmail?.email ||
                                            errorRole?.role
                                        ) {
                                            disabledSave.setTrue();
                                            return;
                                        }

                                        submit();
                                    }}
                                />
                            </div>
                        </DialogActions>
                    </DialogContent>
                </CustomDialog>
            )}
            {showUpdateEmailDialog.value && (
                <CustomDialog
                    onClose={() => {
                        showUpdateEmailDialog.setFalse();
                        showAddUserDialog.setTrue();
                    }}
                    aria-labelledby="update-user-update-email-warning"
                    fullWidth
                    scroll="paper"
                    maxWidth="md"
                    open={open}
                >
                    <DialogTitle id="update-user-update-email-warning-title">
                        {t('uam:updateUserEmail')}
                        <FontAwesomeIcon
                            icon={['fal', 'times']}
                            className="fa-lg close-icon-cv"
                            onClick={() => {
                                showUpdateEmailDialog.setFalse();
                                showAddUserDialog.setTrue();
                            }}
                            data-testid="close-dialog"
                        />
                    </DialogTitle>
                    <DialogContent className="add-risk-dialog-content edit-risk">
                        <p
                            className="update-user-update-email-warning-message mb-10"
                            id="update-user-update-email-warning-message"
                        >
                            {t('uam:ssoOrgUserUpdateEmailWarning')}
                        </p>
                        <DialogActions className="actions-info-wrapper-edit-cv flex-end">
                            <SaveButton
                                id="update-user-update-email-warning-update"
                                className="update-user-update-email-warning-update mt"
                                text="common:update"
                                onClick={() => {
                                    showUpdateEmailDialog.setFalse();
                                    if (validateOnSubmit()) save();
                                    else showAddUserDialog.setTrue();
                                }}
                            />
                        </DialogActions>
                    </DialogContent>
                </CustomDialog>
            )}
        </>
    );
};

export default AddUserModal;
